import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CustomModal from "components/CustomModal";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getClients } from "store/actions/clients";
import { generateProgram } from "store/actions/program";

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = ["Client & Profil", "Objectif", "Programme", "Préférences"];

const GENERATION_STEPS = [
  "Analyse du profil...",
  "Génération de la structure du programme...",
  "Création des séances d'entraînement...",
  "Ajout des exercices...",
  "Finalisation du programme...",
];

const WAIT_MESSAGES = [
  "Interrogation des lois sacrées de la périodisation…",
  "Calcul du ratio souffrance / résultats optimaux…",
  "Vérification que les séances ne sont pas trop sadiques…",
  "Équilibrage chirurgical des charges et repos…",
  "Consultation du grimoire des groupes musculaires…",
  "Validation par l'arbitre des abdos…",
  "Harmonie parfaite push / pull en cours…",
  "Révision finale de la progression des charges…",
  "Vérification que personne ne saute le cardio…",
  "Chef-d'œuvre en construction — encore un instant…",
  "Négociation entre jambes et dos pour savoir qui souffre le plus…",
  "Ajout d'une touche de burpees — juste pour voir…",
  "Suppression des burpees suite à réclamation…",
  "Calcul du nombre de fois où le client dira \"je suis mort\"…",
  "Vérification que le programme ne viole pas la Convention de Genève…",
  "Optimisation du rapport \"je me déteste\" / \"je me sens bien\"…",
  "Consultation des muscles pour obtenir leur consentement…",
  "Synchronisation avec les dieux du gainz…",
  "Recherche du parfait équilibre entre motivation et survie…",
  "Attribution des douleurs musculaires du lendemain…",
  "Vérification que le repos est suffisant pour garder des amis…",
  "Application du principe sacré : ne jamais skip la jambe…",
  "Calcul des calories sacrifiées sur l'autel de la performance…",
  "Alignement des planètes fitness en cours…",
  "Préparation psychologique du client à l'avance…",
  "Mise en garde officielle : résultats peuvent causer excès de confiance…",
  "Dernier contrôle — promis, c'est presque prêt…",
];

const HOME_EQUIPMENT = [
  "Poids du corps", "Haltères", "Kettlebell", "Barre + disques",
  "Élastiques", "Barre de traction", "Banc", "Corde à sauter", "Step", "TRX",
];
const GYM_EQUIPMENT = [
  "Machines guidées", "Haltères / poids libres", "Barres libres",
  "Câbles / poulies", "Barre de traction", "Tapis de course", "Vélo / rameur", "Tout disponible",
];
const TARGET_ZONES = [
  "Corps entier équilibré", "Fessiers", "Cuisses & jambes",
  "Sangle abdominale & ventre", "Dos", "Épaules", "Pectoraux", "Bras",
];

// ─── Shared pill toggle (single-select) ───────────────────────────────────────

const PillGroup = ({ value, onChange, options, fullWidth = false }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_, v) => { if (v !== null) onChange(v); }}
    sx={{
      flexWrap: "wrap",
      gap: 1,
      "& .MuiToggleButtonGroup-grouped": {
        borderRadius: "20px !important",
        border: "1px solid #e2e0d8 !important",
        m: 0,
      },
    }}
  >
    {options.map((opt) => (
      <ToggleButton
        key={opt.value ?? opt}
        value={opt.value ?? opt}
        sx={{
          fontSize: 13,
          px: 2,
          py: 0.6,
          textTransform: "none",
          whiteSpace: "normal",
          textAlign: "left",
          ...(fullWidth ? { width: "100%" } : {}),
          "&.Mui-selected": { bgcolor: "#0933C6", color: "#fff", "&:hover": { bgcolor: "#0929a8" } },
        }}
      >
        {opt.label ?? opt}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

// ─── Multi-select chip pills ───────────────────────────────────────────────────

const MultiChips = ({ value = [], onChange, options, maxSelect }) => {
  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else if (!maxSelect || value.length < maxSelect) onChange([...value, opt]);
  };
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {options.map((opt) => {
        const selected = value.includes(opt);
        const disabled = !selected && maxSelect && value.length >= maxSelect;
        return (
          <Chip
            key={opt}
            label={opt}
            onClick={() => !disabled && toggle(opt)}
            variant={selected ? "filled" : "outlined"}
            sx={{
              fontSize: 12,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.4 : 1,
              bgcolor: selected ? "#0933C6" : "transparent",
              color: selected ? "#fff" : "inherit",
              borderColor: selected ? "#0933C6" : "#e2e0d8",
              "&:hover": selected ? { bgcolor: "#0929a8" } : disabled ? {} : { bgcolor: "action.hover" },
            }}
          />
        );
      })}
    </Box>
  );
};

// ─── Equipment checkbox group ──────────────────────────────────────────────────

const EquipmentGroup = ({ sectionLabel, options, value = [], onChange }) => (
  <Box mb={1.5}>
    <Typography variant="caption" fontWeight={700} color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 0.5 }}>
      {sectionLabel}
    </Typography>
    <FormGroup sx={{ flexDirection: "row", flexWrap: "wrap" }}>
      {options.map((opt) => (
        <FormControlLabel
          key={opt}
          control={
            <Checkbox
              size="small"
              checked={value.includes(opt)}
              onChange={(e) => {
                if (e.target.checked) onChange([...value, opt]);
                else onChange(value.filter((v) => v !== opt));
              }}
            />
          }
          label={<Typography fontSize={13}>{opt}</Typography>}
          sx={{ width: { xs: "100%", sm: "50%" }, mr: 0 }}
        />
      ))}
    </FormGroup>
  </Box>
);

// ─── Question label ────────────────────────────────────────────────────────────

const Q = ({ label, required, hint }) => (
  <Box mb={1}>
    <Typography fontSize={14} fontWeight={500}>
      {label}
      {required && <Typography component="span" color="error" ml={0.3}>*</Typography>}
    </Typography>
    {hint && <Typography fontSize={12} color="text.secondary">{hint}</Typography>}
  </Box>
);

// ─── Generation loader ─────────────────────────────────────────────────────────

const GeneratingLoader = () => {
  const [cur, setCur] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const isOnLastStep = cur === GENERATION_STEPS.length - 1;

  useEffect(() => {
    const t = setInterval(() => setCur((p) => Math.min(p + 1, GENERATION_STEPS.length - 1)), 2500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!isOnLastStep) return;
    const t = setInterval(() => setMsgIdx((p) => (p + 1) % WAIT_MESSAGES.length), 3500);
    return () => clearInterval(t);
  }, [isOnLastStep]);

  return (
    <Box sx={{ py: 2, px: 1 }}>
      {GENERATION_STEPS.map((s, i) => (
        <Stack key={i} direction="row" alignItems="center" gap={1.5} mb={2}>
          {i < cur
            ? <CheckCircleIcon sx={{ color: "#119C83", fontSize: 20, flexShrink: 0 }} />
            : i === cur
            ? <CircularProgress size={18} thickness={5} sx={{ color: "#0933C6", flexShrink: 0 }} />
            : <Box sx={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid #E0E4EA", flexShrink: 0 }} />}
          <Typography sx={{
            fontSize: 14,
            fontWeight: i <= cur ? 600 : 400,
            color: i < cur ? "#119C83" : i === cur ? "#111" : "#9AA0AC",
            transition: "color 0.3s",
          }}>{s}</Typography>
        </Stack>
      ))}
      {isOnLastStep && (
        <Box sx={{ pl: "29.5px", mt: 1, minHeight: 20 }}>
          <Typography key={msgIdx} sx={{
            fontSize: 13,
            color: "text.secondary",
            fontStyle: "italic",
            animation: "fadeIn 0.5s ease-in",
            "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
          }}>
            {WAIT_MESSAGES[msgIdx]}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Initial form values ───────────────────────────────────────────────────────

const INITIAL = {
  client_id: "",
  gender: "Femme",
  technical_mastery: "Intermédiaire",
  body_profile: "",
  discipline: "",
  target_zones: [],
  location: "Salle de sport",
  equipment: [],
  no_of_week: "4",
  session_frequency: "3",
  week_format: "AAAA",
  session_duration: "45-60 min",
  include_warmup: true,
  warmup_type: "Les deux",
  cardio_preference: "Neutre",
  exercise_preference: "Pas de préférence",
  split_type: "Laisser l'IA décider",
  additional_notes: "",
};

// ─── Per-step validation ───────────────────────────────────────────────────────

function validateStep(step, values) {
  const errors = {};
  if (step === 0 && !values.client_id) errors.client_id = "Veuillez sélectionner un client.";
  if (step === 1) {
    if (!values.body_profile) errors.body_profile = "Veuillez sélectionner un profil.";
    if (values.body_profile && values.body_profile !== "Full stretching" && !values.discipline) {
      errors.discipline = "Veuillez sélectionner une discipline.";
    }
  }
  return errors;
}

// ─── Main component ────────────────────────────────────────────────────────────

const GenerateProgramModal = ({ showModal = false, toggleGenerateProgramModal }) => {
  const dispatch = useDispatch();
  const clients = useSelector((state) => state.clients.clients);

  const [step, setStep] = useState(0);
  const [values, setValues] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  useEffect(() => {
    if (showModal) {
      if (clients.length === 0) dispatch(getClients());
      setStep(0);
      setValues(INITIAL);
      setErrors({});
      setIsGenerating(false);
      setGenerationError(null);
    }
  }, [showModal]);

  const set = (field) => (val) => setValues((v) => ({ ...v, [field]: val }));

  const handleNext = () => {
    const errs = validateStep(step, values);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else handleSubmit();
  };

  const handleBack = () => { setErrors({}); if (step > 0) setStep((s) => s - 1); };
  const handleClose = () => { if (!isGenerating) toggleGenerateProgramModal(); };

  const handleSubmit = () => {
    setIsGenerating(true);
    setGenerationError(null);
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + parseInt(values.no_of_week, 10) * 7);
    dispatch(generateProgram(
      { ...values, start_date: startDate.toISOString(), end_date: endDate.toISOString() },
      () => { setIsGenerating(false); toggleGenerateProgramModal(); },
      () => { setIsGenerating(false); setGenerationError(true); },
    ));
  };

  const isFullStretching = values.body_profile === "Full stretching";
  const showHomeEquipment = values.location === "Maison" || values.location === "Les deux";
  const showGymEquipment = values.location === "Salle de sport" || values.location === "Les deux";
  const selectedClient = clients.find((c) => c._id === values.client_id) || null;

  const weekFormatOptions = [
    { value: "AAAA", label: "AAAA — Même programme toutes les semaines", hint: "Recommandé pour les débutants" },
    { value: "AABB", label: "AABB — 2 programmes alternés par blocs de 2 semaines", disabled: values.no_of_week === "2" },
    { value: "ABAB", label: "ABAB — 2 programmes alternés semaine par semaine" },
  ];

  const modalTitle = isGenerating ? "Génération en cours" : generationError ? "Génération échouée" : STEPS[step];

  return (
    <CustomModal
      open={showModal}
      onCloseHandler={handleClose}
      modalWidth="600px"
      title={modalTitle}
    >
      {isGenerating ? (
        <GeneratingLoader />
      ) : generationError ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography fontSize={40} mb={1}>🤖</Typography>
          <Typography fontWeight={600} fontSize={17} mb={1}>
            Oups, la génération n'a pas abouti
          </Typography>
          <Typography color="text.secondary" fontSize={14} mb={3} maxWidth={400} mx="auto">
            Notre IA est encore en phase bêta et peut parfois avoir besoin d'une deuxième tentative.
            Tes paramètres sont conservés — réessaie dans quelques instants.
          </Typography>
          <Stack direction="row" gap={1.5} justifyContent="center">
            <Button variant="outlined" onClick={handleClose}>Fermer</Button>
            <Button variant="contained" onClick={handleSubmit}>Réessayer</Button>
          </Stack>
        </Box>
      ) : (
        <Box>
          {/* Progress — compact dots on mobile, full stepper on desktop */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography fontSize={12} color="text.secondary">
                  Étape {step + 1} / {STEPS.length}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {STEPS[step]}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={((step + 1) / STEPS.length) * 100}
                sx={{ borderRadius: 1, height: 4 }}
              />
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Stepper activeStep={step} alternativeLabel>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel sx={{ "& .MuiStepLabel-label": { fontSize: 11 } }}>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>

          {/* Step content — scrollable on mobile */}
          <Box sx={{ overflowY: "auto", maxHeight: { xs: "58vh", sm: "none" } }}>

            {/* ── Step 0: Client & Profil ── */}
            {step === 0 && (
              <Box>
                <Box mb={3}>
                  <Q label="Client" required />
                  <Autocomplete
                    options={clients}
                    value={selectedClient}
                    onChange={(_, v) => set("client_id")(v?._id || "")}
                    getOptionLabel={(o) => `${o.first_name || ""} ${o.last_name || ""}`.trim()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Rechercher un client..."
                        size="small"
                        error={!!errors.client_id}
                        helperText={errors.client_id}
                      />
                    )}
                    noOptionsText="Aucun client trouvé"
                  />
                </Box>

                <Box mb={3}>
                  <Q label="Genre" />
                  <PillGroup value={values.gender} onChange={set("gender")} options={["Femme", "Homme"]} />
                </Box>

                <Box>
                  <Q label="Maîtrise technique" />
                  <PillGroup
                    value={values.technical_mastery}
                    onChange={set("technical_mastery")}
                    fullWidth
                    options={[
                      { value: "Débutant", label: "Débutant — ne connaît pas les machines ni les mouvements de base" },
                      { value: "Intermédiaire", label: "Intermédiaire — à l'aise sur machines guidées et mouvements fondamentaux" },
                      { value: "Avancé", label: "Avancé — maîtrise les poids libres, barres et mouvements complexes" },
                    ]}
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 1: Objectif ── */}
            {step === 1 && (
              <Box>
                <Box mb={3}>
                  <Q label="Profil & objectif" required />
                  <PillGroup
                    value={values.body_profile}
                    onChange={(v) => { set("body_profile")(v); if (v === "Full stretching") set("discipline")(""); }}
                    fullWidth
                    options={[
                      { value: "En surpoids", label: "En surpoids — veut perdre du gras et se tonifier" },
                      { value: "Skinny fat", label: "Skinny fat — maigre et gras à la fois, veut recomposer son corps" },
                      { value: "Mince", label: "Mince — veut prendre de la masse musculaire" },
                      { value: "Full stretching", label: "Full stretching — programme dédié aux étirements et à la flexibilité" },
                    ]}
                  />
                  {errors.body_profile && <Typography color="error" fontSize={12} mt={0.5}>{errors.body_profile}</Typography>}
                </Box>

                {isFullStretching ? (
                  <Alert icon={<InfoOutlinedIcon fontSize="small" />} severity="info">
                    Le programme Full stretching n'est pas encore pris en charge par la génération IA. Veuillez créer ce programme manuellement.
                  </Alert>
                ) : (
                  <>
                    {values.body_profile && (
                      <Box mb={3}>
                        <Q label="Discipline principale" required />
                        <PillGroup
                          value={values.discipline}
                          onChange={set("discipline")}
                          options={["Musculation", "Cardio / HIIT", "Musculation + Cardio HIIT", "Mobilité / Stretching", "Yoga", "Pilates"]}
                        />
                        {errors.discipline && <Typography color="error" fontSize={12} mt={0.5}>{errors.discipline}</Typography>}
                      </Box>
                    )}

                    <Box>
                      <Q label="Zone à cibler" hint={`Jusqu'à 2 zones — ${values.target_zones.length}/2 sélectionnée(s)`} />
                      <MultiChips value={values.target_zones} onChange={set("target_zones")} options={TARGET_ZONES} maxSelect={2} />
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* ── Step 2: Programme ── */}
            {step === 2 && (
              <Box>
                <Box mb={3}>
                  <Q label="Lieu d'entraînement" />
                  <PillGroup
                    value={values.location}
                    onChange={(v) => { set("location")(v); set("equipment")([]); }}
                    options={["Salle de sport", "Maison", "Les deux"]}
                  />
                </Box>

                {(showHomeEquipment || showGymEquipment) && (
                  <Box mb={3}>
                    <Q label="Matériel disponible" />
                    {showHomeEquipment && <EquipmentGroup sectionLabel="Maison" options={HOME_EQUIPMENT} value={values.equipment} onChange={set("equipment")} />}
                    {showGymEquipment && <EquipmentGroup sectionLabel="Salle" options={GYM_EQUIPMENT} value={values.equipment} onChange={set("equipment")} />}
                  </Box>
                )}

                <Box mb={3}>
                  <Q label="Durée totale" />
                  <PillGroup
                    value={values.no_of_week}
                    onChange={(v) => { set("no_of_week")(v); if (v === "2" && values.week_format === "AABB") set("week_format")("AAAA"); }}
                    options={[
                      { value: "2", label: "2 semaines" },
                      { value: "4", label: "4 semaines" },
                      { value: "8", label: "8 semaines" },
                      { value: "12", label: "12 semaines" },
                    ]}
                  />
                </Box>

                <Box mb={3}>
                  <Q label="Fréquence hebdomadaire" />
                  <PillGroup
                    value={values.session_frequency}
                    onChange={set("session_frequency")}
                    options={["2", "3", "4", "5", "6"].map((v) => ({ value: v, label: `${v}x / semaine` }))}
                  />
                </Box>

                <Box mb={3}>
                  <Q label="Format des semaines" />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {weekFormatOptions.map((opt) => (
                      <Box key={opt.value}>
                        <ToggleButton
                          value={opt.value}
                          selected={values.week_format === opt.value}
                          disabled={opt.disabled}
                          onChange={() => !opt.disabled && set("week_format")(opt.value)}
                          sx={{
                            width: "100%",
                            justifyContent: "flex-start",
                            textAlign: "left",
                            fontSize: 13,
                            px: 2,
                            py: 0.8,
                            textTransform: "none",
                            borderRadius: "8px !important",
                            border: "1px solid #e2e0d8 !important",
                            "&.Mui-selected": { bgcolor: "#0933C6", color: "#fff" },
                            "&.Mui-disabled": { opacity: 0.4 },
                          }}
                        >
                          <Box sx={{ textAlign: "left" }}>
                            {opt.label}
                            {opt.hint && (
                              <Typography component="span" fontSize={11} ml={1}
                                sx={{ color: values.week_format === opt.value ? "rgba(255,255,255,0.75)" : "text.secondary" }}>
                                {opt.hint}
                              </Typography>
                            )}
                            {opt.disabled && values.no_of_week === "2" && (
                              <Typography component="span" fontSize={11} ml={1} sx={{ color: "text.disabled" }}>
                                (requiert ≥ 4 semaines)
                              </Typography>
                            )}
                          </Box>
                        </ToggleButton>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Q label="Durée par séance" />
                  <PillGroup
                    value={values.session_duration}
                    onChange={set("session_duration")}
                    options={["< 15 min", "15-30 min", "30-45 min", "45-60 min", "60-75 min", "75 min+"]}
                  />
                </Box>
              </Box>
            )}

            {/* ── Step 3: Préférences ── */}
            {step === 3 && (
              <Box>
                <Box mb={3}>
                  <Q label="Inclure un échauffement ?" />
                  <PillGroup
                    value={values.include_warmup ? "Oui" : "Non"}
                    onChange={(v) => set("include_warmup")(v === "Oui")}
                    options={["Oui", "Non"]}
                  />
                </Box>

                {values.include_warmup && (
                  <Box mb={3}>
                    <Q label="Type d'échauffement" />
                    <PillGroup
                      value={values.warmup_type}
                      onChange={set("warmup_type")}
                      fullWidth
                      options={[
                        { value: "Articulaire", label: "Échauffement articulaire" },
                        { value: "Cardio-musculaire", label: "Échauffement cardio-musculaire" },
                        { value: "Les deux", label: "Les deux" },
                      ]}
                    />
                  </Box>
                )}

                <Box mb={3}>
                  <Q label="Rapport au cardio long" hint="Marche, vélo, rameur…" />
                  <PillGroup
                    value={values.cardio_preference}
                    onChange={set("cardio_preference")}
                    fullWidth
                    options={[
                      { value: "Adore", label: "Adore — en inclure le plus possible" },
                      { value: "Neutre", label: "Neutre — le nécessaire suffit" },
                      { value: "Déteste", label: "Déteste — réduire au minimum" },
                      { value: "Pas de cardio long", label: "Pas de cardio long" },
                    ]}
                  />
                </Box>

                <Box mb={3}>
                  <Q label="Type d'exercices préféré" />
                  <PillGroup
                    value={values.exercise_preference}
                    onChange={set("exercise_preference")}
                    options={["Machines", "Poids libres", "Mix machines + poids libres", "Poids du corps", "Pas de préférence"]}
                  />
                </Box>

                <Box mb={3}>
                  <Q label="Organisation des séances" />
                  <PillGroup
                    value={values.split_type}
                    onChange={set("split_type")}
                    fullWidth
                    options={[
                      { value: "Full body", label: "Full body — même chose à chaque séance" },
                      { value: "Haut du corps / Bas du corps", label: "Haut du corps / Bas du corps" },
                      { value: "Push / Pull / Legs", label: "Push / Pull / Legs" },
                      { value: "Par groupe musculaire", label: "Par groupe musculaire (Pecs+Tri / Dos+Bi / Épaules / Jambes…)" },
                      { value: "Laisser l'IA décider", label: "Laisser l'IA décider selon la fréquence et l'objectif" },
                    ]}
                  />
                </Box>

                <Box>
                  <Q label="Informations complémentaires" hint="Optionnel — blessures, contraintes, sport en parallèle…" />
                  <TextField
                    value={values.additional_notes}
                    onChange={(e) => set("additional_notes")(e.target.value)}
                    placeholder="Ex : douleur au genou droit, pratique le tennis le week-end"
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Navigation */}
          <Stack direction={{ xs: "column-reverse", sm: "row" }} gap={1.5} mt={3}>
            <Button
              fullWidth
              color="secondary"
              variant="outlined"
              onClick={step === 0 ? handleClose : handleBack}
            >
              {step === 0 ? "Annuler" : "Précédent"}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleNext}
              disabled={isFullStretching && step === 1}
              startIcon={step === STEPS.length - 1 ? <AutoAwesomeIcon /> : null}
            >
              {step === STEPS.length - 1 ? "Générer" : "Suivant"}
            </Button>
          </Stack>
        </Box>
      )}
    </CustomModal>
  );
};

export default GenerateProgramModal;
